import React, { useMemo, useState } from 'react';
import { BuroPortal, AVISO_SIN_CLIENTE } from './helpers';
import { AvisoCodigoPostal, BuroCampo } from './BuroCampo';
import { useAutollenadoDomicilio } from './useCodigoPostal';
import { clavesDomicilio, faltantesLista, obligatoriosLista } from './columnasPm';
import { erroresDe } from './columnas';
import { agregarEntrada, cambiosLista, indicesLista, quitarEntrada } from './listas';

const ROJO = '#B91C1C';
const ORDEN_DOMICILIO = ['cp', 'estado', 'municipio', 'ciudad', 'colonia', 'calle', 'complemento'];

// Mínimo obligatorio de una entrada, en vivo: etiquetas con * (`minimo`) y las que siguen vacías (`faltan`).
const minimoDe = (segmento, columnas, indice, valores) => {
  const propias = columnas.filter((c) => c.indice === indice && !c.fija);
  const base = propias.length ? Number(propias[0].k) - Number(propias[0].etiqueta) : 0;
  const get = (e) => valores[String(base + e)];
  return {
    propias,
    minimo: obligatoriosLista(segmento, get),
    faltan: faltantesLista(segmento, get, new Set(propias.map((c) => Number(c.etiqueta)))),
  };
};

// Campos de una entrada (accionista o aval). Su C.P. completo llena estado,
// municipio, ciudad y colonia de esa misma entrada.
const CamposEntrada = ({ fila, segmento, propias, minimo, faltan, intento, valores, setValores, guardando }) => {
  const [tocados, setTocados] = useState(() => new Set());
  const colCp = propias.find((c) => c.dom === 'cp');
  const claves = useMemo(() => clavesDomicilio(colCp), [colCp]);
  const cp = useAutollenadoDomicilio(colCp ? valores[colCp.k] : '', claves, setValores);
  const rojos = new Set(propias.filter((c) => faltan.includes(Number(c.etiqueta)) && (intento || tocados.has(c.k))).map((c) => c.k));
  const campo = (c) => <label className="bu-field" key={c.k} onBlur={() => setTocados((t) => new Set(t).add(c.k))}>
    <span className="bu-label">{c.label} · {c.num}{minimo.has(Number(c.etiqueta)) && <span style={{ color: ROJO }}> *</span>}</span>
    <BuroCampo col={c} value={valores[c.k] ?? ''} onChange={(v) => setValores((prev) => ({ ...prev, [c.k]: v }))} disabled={guardando} invalid={faltan.includes(Number(c.etiqueta))} className={faltan.includes(Number(c.etiqueta)) ? 'bu-input bu-req-falta' : 'bu-input'} colonias={c.dom === 'colonia' && cp.estado === 'ok' ? cp.datos.colonias : null} />
    {c.dom === 'cp' && cp.estado !== 'ok' && <AvisoCodigoPostal cp={cp} />}
    {rojos.has(c.k) && <span className="bu-msg err">Dato obligatorio</span>}
    {erroresDe(fila, c).map((e, i) => <span className="bu-msg err" key={i}>{e.mensaje}</span>)}
  </label>;
  return <>
    {faltan.length > 0 && <div style={{ color: ROJO, fontSize: 12, fontWeight: 600 }}>Faltan: {propias.filter((c) => faltan.includes(Number(c.etiqueta))).map((c) => c.label).join(', ')}</div>}
    <div className="bu-sub" style={{ fontSize: 12 }}>{segmento === 'AC'
      ? '* mínimo obligatorio · RFC desde 11% · si capturas un dato del domicilio, completa todo el domicilio'
      : '* mínimo obligatorio · RFC y domicilio completo siempre'}</div>
    {propias.map((c, i) => {
      if (!c.dom) return campo(c);
      // El domicilio se pinta junto, en el lugar de su primera columna: C.P., estado, municipio, ciudad, colonia, calle, complemento.
      if (i !== propias.findIndex((x) => x.dom)) return null;
      return <div key="domicilio" className="bu-field" style={{ gap: 10 }}>
        <div className="bu-label">Domicilio</div>
        {ORDEN_DOMICILIO.map((r) => propias.find((x) => x.dom === r)).filter(Boolean).map((x) => <React.Fragment key={x.k}>
          {campo(x)}
          {x.dom === 'cp' && cp.estado === 'ok' && <div className="bu-note bu-note-info" style={{ fontSize: 12 }}>
            <i className="pi pi-info-circle" />
            <span>C.P. encontrado: {[cp.datos.estado, cp.datos.municipio, cp.datos.ciudad].filter(Boolean).join(' · ')}</span>
          </div>}
        </React.Fragment>)}
      </div>;
    })}
  </>;
};

export const BuroListaEditor = ({ fila, segmento, columnas, guardando, error, onClose, onGuardar }) => {
  const [valores, setValores] = useState(() => ({ ...fila.valores }));
  const [indices, setIndices] = useState(() => indicesLista(fila.valores, columnas));
  const [sistema, setSistema] = useState(false);
  const [intento, setIntento] = useState(false);
  const nombre = segmento === 'AC' ? 'Accionistas' : 'Avales';
  // Mínimo obligatorio de cada entrada: mientras falte algo, no se guarda (una vacía también bloquea).
  const minimos = Object.fromEntries(indices.map((i) => [i, minimoDe(segmento, columnas, i, valores)]));
  const incompleta = indices.some((i) => minimos[i].faltan.length > 0);
  const quitar = (indice) => {
    setValores((v) => quitarEntrada(v, columnas, indice));
    setIndices((v) => v.filter((i) => i !== indice));
  };
  return (
    <BuroPortal><div className="bu-panel" style={{ width: 540 }}>
      <div className="bu-panel-head"><h3>{nombre} · {fila.numero_cuenta}</h3><button type="button" className="bu-btn" onClick={onClose}>Cerrar</button></div>
      <div className="bu-panel-body">
        {error && <div role="alert" className="bu-note bu-note-err">{error}</div>}
        {indices.map((indice) => <fieldset key={indice} disabled={guardando}>
          <legend>{nombre === 'Accionistas' ? 'Accionista' : 'Aval'} {indice + 1}</legend>
          <CamposEntrada fila={fila} propias={minimos[indice].propias} minimo={minimos[indice].minimo} faltan={minimos[indice].faltan} intento={intento} segmento={segmento} valores={valores} setValores={setValores} guardando={guardando} />
          <button type="button" className="bu-btn bu-btn-danger" onClick={() => quitar(indice)}>Quitar {indice + 1}</button>
        </fieldset>)}
        {!indices.length && <div className="bu-sub">No hay {nombre.toLowerCase()} capturados.</div>}
        <button type="button" className="bu-btn" disabled={guardando || indices.length >= 10 || !columnas.length} onClick={() => setIndices(agregarEntrada)}>Agregar {segmento === 'AC' ? 'accionista' : 'aval'}</button>
        <div className="bu-sub">Máximo 10 entradas. Se guarda la lista completa.</div>
        {fila.cliente_ligado ? <label className="bu-check"><input type="checkbox" checked={sistema} onChange={(e) => setSistema(e.target.checked)} />Corregir también en el sistema</label> : <div className="bu-note bu-note-warn">{AVISO_SIN_CLIENTE}</div>}
        {segmento === 'AC' && <div className="bu-sub">Los accionistas se actualizan en todos los contratos de la empresa.</div>}
      </div>
      <div className="bu-panel-foot"><button type="button" className="bu-btn" onClick={onClose}>Cancelar</button><span title={incompleta ? `Completa los datos mínimos de cada ${segmento === 'AC' ? 'accionista' : 'aval'}` : undefined} onClick={() => { if (incompleta) setIntento(true); }}><button type="button" className="bu-btn bu-btn-primary" disabled={guardando || !columnas.length || incompleta} title={incompleta ? `Completa los datos mínimos de cada ${segmento === 'AC' ? 'accionista' : 'aval'}` : undefined} onClick={() => onGuardar(cambiosLista(valores, columnas.filter((c) => !c.fija), indices), sistema)}>{guardando ? 'Guardando…' : 'Guardar lista'}</button></span></div>
    </div></BuroPortal>
  );
};
